import { useState, useEffect } from 'react';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken, getPortalType } from '@/utils/auth';

export default function ChangePasswordModal({ isOpen, onClose, onSuccess, isFirstTime = false, portalOverride, requireEmail = false }) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState({ old: false, n: false, c: false });

  useEffect(() => {
    if (!isOpen || !isFirstTime) return;
    const handler = (e) => {
      if (e.key === 'Escape' && isFirstTime) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, isFirstTime]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!oldPassword || !newPassword || !confirmPassword) return setError('All fields are required');
    if (requireEmail && (!email || !email.includes('@'))) return setError('A valid email is required');
    if (newPassword !== confirmPassword) return setError('New password and confirm password do not match');
    if (newPassword.length < 8) return setError('Password must be at least 8 characters long');

    setLoading(true);
    try {
      const portal = portalOverride ?? getPortalType(window.location.pathname || '');
      const token = getAuthToken(portal);
      const res = await axios.post(
        apiUrl('users/change-password/'),
        {
          old_password: oldPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
          email: requireEmail ? email : undefined
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (res.status === 200) {
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setEmail('');
        onSuccess();
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) setError(err.response.data?.error || 'Failed to change password');
      else setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const backdropClick = (e) => {
    if (isFirstTime) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={backdropClick}>
      <div className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-foreground">{isFirstTime ? 'Change Your Password' : 'Change Password'}</h2>
          {!isFirstTime && (
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close" disabled={loading}>✕</button>
          )}
        </div>

        {isFirstTime && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/50 rounded-lg">
            <p className="text-sm text-yellow-400 font-medium">⚠️ You must change your password before continuing.</p>
            {requireEmail && (
              <p className="text-xs text-yellow-400/80 mt-1">Please also provide your email for account recovery.</p>
            )}
          </div>
        )}

        {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg"><p className="text-sm text-red-400">{error}</p></div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          {requireEmail && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="block w-full px-4 py-2 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] focus:border-[hsl(var(--green-bright))] text-foreground placeholder:text-muted-foreground"
                required
                disabled={loading}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Current Password</label>
            <div className="relative">
              <input type={show.old ? 'text' : 'password'} value={oldPassword} onChange={(e) => setOldPassword(e.target.value)}
                className="block w-full px-4 py-2 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] focus:border-[hsl(var(--green-bright))] text-foreground placeholder:text-muted-foreground" required disabled={loading} />
              <button type="button" onClick={() => setShow(s => ({ ...s, old: !s.old }))} className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground">
                {show.old ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">New Password</label>
            <div className="relative">
              <input type={show.n ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="block w-full px-4 py-2 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] focus:border-[hsl(var(--green-bright))] text-foreground placeholder:text-muted-foreground" required disabled={loading} minLength={8} />
              <button type="button" onClick={() => setShow(s => ({ ...s, n: !s.n }))} className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground">
                {show.n ? '🙈' : '👁️'}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Must be at least 8 characters long</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Confirm New Password</label>
            <div className="relative">
              <input type={show.c ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                className="block w-full px-4 py-2 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] focus:border-[hsl(var(--green-bright))] text-foreground placeholder:text-muted-foreground" required disabled={loading} minLength={8} />
              <button type="button" onClick={() => setShow(s => ({ ...s, c: !s.c }))} className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground">
                {show.c ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            {!isFirstTime && (
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-[hsl(var(--card-border))] rounded-lg text-foreground hover:bg-[hsl(var(--sidebar-hover))]" disabled={loading}>
                Cancel
              </button>
            )}
            <button type="submit" className="flex-1 px-4 py-2 bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white rounded-lg hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] disabled:opacity-50 transition-all duration-200" disabled={loading}>
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


